import React from 'react';

import Context     from '../../../../../../context';
import validations from '../../../../../../validations';

import { useFormik         } from 'formik';
import { useTranslation    } from 'react-i18next';
import { locationNamespace } from '../../../../../../types/location.namespace';

import {
  CustomInput,
  CustomModal,
  CustomButton2,
  CustomLeaflet,
  ResponsiveView, LocationDropdowns,
} from '../../../../../../components';

import './index.sass';
import CustomCheckbox from "../../../../../../components/CustomCheckbox";

const Update: React.FC = (): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const [location, setLocation] = React.useState<Array<number>>([]);
  const { showModal, setShowModal, updateFunction, isLoading, selectedCity } = React.useContext(Context.Location.City);
  const [selectedProvince, setSelectedProvince] = React.useState<locationNamespace.province.index | undefined>(
    selectedCity?.province
  );
  const { t } = useTranslation();

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  const formik = useFormik<locationNamespace.city.create>({
    enableReinitialize: true,
    initialValues: {
      isCapital: selectedCity?.isCapital,

      key        : selectedCity?.key,
      name       : selectedCity?.name || '',
      prefix     : selectedCity?.prefix,
      description: selectedCity?.description,

      code      : selectedCity?.code,
      population: selectedCity?.population,

      location: {
        type: 'Point',
        coordinates: [
          Number(selectedCity?.location?.coordinates[0]),
          Number(selectedCity?.location?.coordinates[1]),
        ]
      }
    },
    validationSchema: validations.location.province.create,
    onSubmit: (values: locationNamespace.city.create, { setSubmitting }) => {
      const newValues: locationNamespace.city.create = {
        ...values,
        province : selectedProvince ? selectedProvince?._id : selectedCity?.province._id,
        location: {
          type: 'Point',
          coordinates: [
            Number(location[0]),
            Number(location[1]),
          ]
        }
      }
      Promise.resolve(updateFunction!(newValues)).catch((e) => setSubmitting(false));
    },
  });

  async function submitHandler(): Promise<void> {
    await formik.submitForm();
  }

  function handleChangeLocation(lat: any, lng: any): void {
    setLocation([lat, lng]);
  }

  function onSelectProvince(province: locationNamespace.province.index): void {
    if (!province) return;
    setSelectedProvince(province);
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------ Start UseEffect functions --------------------------------------------

  React.useEffect((): void => {
    if (selectedCity?.location) {
      setLocation([
        Number(selectedCity?.location?.coordinates[0]),
        Number(selectedCity?.location?.coordinates[1]),
      ]);
    }
  }, [selectedCity]);

  // ! ------------------------------------------- End UseEffect functions ---------------------------------------------
  // ! =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <section id="parent-update-modal-container">
      <form onSubmit={submitHandler}>
        <CustomModal
          maxWidth={700}
          label='ویرایش شهر'
          open={showModal === 'UPDATE'}
          onClose={() => setShowModal!('NONE')}
        >
          <ResponsiveView.Container className="create-form-content">
            <br/>
            <hr/>
            <ResponsiveView.Row>
              <ResponsiveView.Column medium={6}>
                <LocationDropdowns.Province
                  onSelectItem = { onSelectProvince }
                  defaultItem  = { selectedProvince ? selectedProvince : selectedCity?.province }
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <hr/>
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id='name'
                  name='name'
                  label={'نام شهر'}
                  value={formik.values.name}
                  error={formik.errors.name && formik.touched.name}
                  errorText={formik.errors.name}
                  formikProps={{onBlur: formik.handleBlur}}
                  onValueChange={formik.handleChange}
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column medium={6}>
                <CustomCheckbox label='مرگز استان میباشد'/>
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <br/>
            <ResponsiveView.Row className='form-row'>
              <ResponsiveView.Column small={12}>
                <CustomLeaflet
                  onChangeLatAndLng={handleChangeLocation}
                  defaultLat={location[0]}
                  defaultLng={location[1]}
                  defaultLatArea={selectedCity?.location ? selectedCity?.location.coordinates[0] : selectedProvince?.location?.coordinates[0]}
                  defaultLngArea={selectedCity?.location ? selectedCity?.location.coordinates[1] : selectedProvince?.location?.coordinates[1]}
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <br />
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id="key"
                  key="key"
                  label={'شناسه شهر'}
                  value={formik.values.key}
                  onValueChange={formik.handleChange}
                  formikProps={{onBlur: formik.handleBlur}}
                  error={formik.errors.key && formik.touched.key}
                  errorText={formik.errors.key}
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id="prefix"
                  key="prefix"
                  label={'پیش شماره‌ی شهر'}
                  value={formik.values.prefix}
                  onValueChange={formik.handleChange}
                  formikProps={{onBlur: formik.handleBlur}}
                  error={formik.errors.prefix && formik.touched.prefix}
                  errorText={formik.errors.prefix}
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <hr/>
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column large={6} medium={6} small={12}>
                <CustomButton2
                  isLoading={isLoading}
                  onClick={() => setShowModal!("NONE")}
                  title={t("cancel")}
                  type="danger"
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column large={6} medium={6} small={12}>
                <CustomButton2 isLoading={isLoading} onClick={submitHandler} title={t("submit")}/>
              </ResponsiveView.Column>
            </ResponsiveView.Row>
          </ResponsiveView.Container>
        </CustomModal>
      </form>
    </section>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================

};

export default Update;
